import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useViewOnly } from '@/components/templates/app-shell'
import { DataTableBlock } from '@/components/templates/list-page'
import { TooltipProvider } from '@/components/ui/tooltip'
import { getJSON } from '@/lib/api'
import { RosterToolbar } from './RosterToolbar'
import { initialRosterState, rosterColumns, type RosterState } from './roster-columns'
import type { Enrolment } from './enrolment'

export function CourseRoster({
  courseId,
  unit,
  onEdit,
  onDelete,
  focusEnrolmentId,
}: {
  courseId: number
  unit: string
  onEdit: (row: Enrolment) => void
  onDelete: (row: Enrolment) => void
  focusEnrolmentId?: number
}) {
  const [state, setState] = useState(initialRosterState)
  const focused = useRef(false)
  const viewOnly = useViewOnly()
  const update = (patch: Partial<RosterState>) => setState((current) => ({ ...current, ...patch }))
  const query = useQuery({
    queryKey: ['enrolments', 'course', courseId, state],
    queryFn: () =>
      getJSON<{ enrolments: Enrolment[] }>(
        `/api/v1/courses/${courseId}/enrolments?${new URLSearchParams({ ...state })}`,
      ),
  })
  const owners = useQuery({
    queryKey: ['enrolments', 'course-owners', courseId],
    queryFn: () => getJSON<{ enrolments: Enrolment[] }>(`/api/v1/courses/${courseId}/enrolments`),
  })
  const toggleSort = (sort: string) =>
    update({ sort, direction: state.sort === sort && state.direction === 'asc' ? 'desc' : 'asc' })
  const filtered = Boolean(state.search || state.status || state.owner)
  const rows = query.data?.enrolments ?? []
  useEffect(() => {
    if (focused.current) return
    const id = focusEnrolmentId ?? Number(window.location.hash.replace('#enrolment-', ''))
    const element = document.getElementById(`enrolment-${id}`)
    element?.focus()
    element?.scrollIntoView?.({ block: 'nearest' })
    if (element) focused.current = true
  }, [focusEnrolmentId, query.data])
  return (
    <section className="space-y-3" aria-labelledby="enrolments-heading">
      <h2 id="enrolments-heading" className="text-body font-semibold">
        Enrolments{query.data && ` · ${rows.length}`}
      </h2>
      <RosterToolbar
        state={state}
        update={update}
        rows={owners.data?.enrolments ?? []}
        exportPath={`/api/v1/courses/${courseId}/enrolments/export`}
      />
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <TooltipProvider>
          <DataTableBlock
            table={{
              rows,
              columns: rosterColumns(state, toggleSort, unit, { viewOnly, onEdit, onDelete }),
              getRowId: (row) => row.id,
              getRowLabel: (row) => `Enrolment for ${row.person_name}`,
              state: query.isPending ? 'loading' : query.isError ? 'error' : 'ready',
              loadingTitle: 'Loading enrolments',
              errorTitle: 'Enrolments unavailable',
              errorDescription: query.error?.message,
              emptyTitle: filtered ? 'No matching enrolments' : 'No enrolments yet.',
              emptyVariant: filtered ? 'default' : 'quiet',
            }}
          />
        </TooltipProvider>
      </div>
    </section>
  )
}
